import * as React from 'react'
import { useMemo } from 'react'
import {
    Accordion,
    AccordionHeader,
    AccordionItem,
    AccordionPanel,
    Field,
    InteractionTag,
    InteractionTagPrimary,
    makeStyles,
    tokens,
} from '@fluentui/react-components'
import { CalendarDayRegular, CalendarMonthRegular, CalendarWorkWeekRegular } from '@fluentui/react-icons'
import useModules from '../../hooks/module/useModules'
import { useTranslation } from 'react-i18next'
import useViewport, { Viewport } from '../../hooks/useViewport'
import UserPicker from '../../components/TagPicker/UserPicker'
import { ModuleType } from '../../context/GlobalContext'
import AttributesList from '../../components/Form/Attributes/AttributesList'
import { AttributeContextType, AttributeEntityDto, AttributeViewType, EntityAttributeType } from '../../hooks/useAttributes'
import { useFormikContext } from 'formik'
import useAxios from '../../hooks/useAxios'
import { SortDirection } from '../../config/settingConfig'
import { AxiosResponse } from 'axios'
import BaseOverview, {
    BaseOverviewCount,
    BaseOverviewLineItemProps,
    BaseOverviewProps,
    parseFilterValuesToParams,
    useOverviewStyles,
} from '../BaseOverview/BaseOverview'
import { BaseOverviewFilterProps, BaseOverviewFilterType, generateFromDate, generateToDate } from '../BaseOverview/BaseOverviewFilter'
import DateRange from '../../components/DatePicker/DateRange'
import BooleanCombobox from '../../components/Combobox/BooleanCombobox'
import FilePicker from '../../components/TagPicker/FilePicker'
import { CheckboxOnChangeData } from '@fluentui/react-checkbox'
import { DateTime } from 'luxon'
import { FixedPriceOverviewDto } from '../../config/registrationConfig'
import { MoneyDto } from '../../config/i18nConfig'
import { BaseOverviewSortingType } from '../BaseOverview/BaseSortMenu'
import FixedPriceListItem from './FixedPriceListItem'
import FixedPriceHeaderItem from './FixedPriceHeaderItem'

export const useItemStyles = makeStyles({
    listFixedPriceItem: {
        gridTemplateColumns: '32px 70px 5fr auto 80px 80px',
    },
    listFixedPriceItemSelectable: {
        gridTemplateColumns: '32px 32px 70px 5fr auto 80px 80px',
    },
    listFixedPriceItemSmall: {
        gridTemplateColumns: '70px 1fr',
        gridTemplateRows: 'auto 1fr',
    },
    listFixedPriceItemSmallSelectable: {
        gridTemplateColumns: '32px 70px 1fr',
        gridTemplateRows: 'auto 1fr',
    },
    listFixedPriceItemSmallBottomRow: {
        display: 'grid',
        gridTemplateColumns: 'auto 80px 80px',
        gap: tokens.spacingVerticalS,
    },
    listFixedPriceItemOverflow: {
        overflowWrap: 'anywhere',
    },
    turnoverField: {
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: tokens.spacingVerticalS,
    },
})

type BaseItem = FixedPriceOverviewDto

export type OverviewItem = {
    // Is calculated during runtime and not returned by the API. Expressed in American number notation (0.00).
    turnover: MoneyDto
    invoiceTurnover: MoneyDto
} & BaseItem

export type FixedPriceOverviewProps = BaseOverviewProps<
    ExtendedFixedPriceOverviewDto,
    FixedPriceFilterType,
    FixedPriceSortingType,
    FixedPriceOverviewCount
>

export type BaseFixedPriceOverviewProps = Partial<FixedPriceOverviewProps> & {
    baseUrl: string

    fileId?: string
    hideFileField?: boolean
    hideInvoiceField?: boolean
    renderLink?: boolean
}

export type BaseFixedPriceLineOverviewProps = BaseFixedPriceOverviewProps &
    BaseOverviewLineItemProps<ExtendedFixedPriceOverviewDto, FixedPriceFilterType, FixedPriceSortingType>

export type FixedPriceOverviewCount = BaseOverviewCount & {
    currencies: FixedPriceCurrencyOverviewCount[]
}

export type FixedPriceCurrencyOverviewCount = {
    currency: string
    distance: number
    turnover: string
    invoiceTurnover: string
}

export type ExtendedFixedPriceOverviewDto = FixedPriceOverviewDto & {
    // Is calculated during runtime and not actually returned by the API. Expressed in American number notation (0.00).
    turnover: MoneyDto
    invoiceTurnover: MoneyDto
}

export type FixedPriceFilterType = AttributeEntityDto &
    BaseOverviewFilterType & {
        from?: string
        to?: string
        users?: string[]
        isBilled?: boolean
        files?: string[]
        fileOwners?: string[]
        invoiceApprovals?: string[]
    }

export type FixedPriceSorting = {
    id: string
    date: string
    description: string
    'file.name': string
    'file.reference': string
}

export type FixedPriceSortingType = BaseOverviewSortingType<FixedPriceSorting>

type BaseTaskOverviewFilterProps = BaseFixedPriceOverviewProps &
    BaseOverviewFilterProps<ExtendedFixedPriceOverviewDto, FixedPriceFilterType, FixedPriceSortingType>

function FixedPriceFilter(props: BaseTaskOverviewFilterProps) {
    const { values, setFieldValue } = useFormikContext<FixedPriceFilterType>()
    const modules = useModules()
    const { t: translate } = useTranslation()
    const { viewport, isViewport } = useViewport()

    const orientation = useMemo(() => (isViewport('<', Viewport.Large) ? 'vertical' : 'horizontal'), [viewport])

    const updateFieldValue = (field: string, value: any, shouldValidate?: boolean) => {
        setFieldValue('tag', undefined, false).then(() => setFieldValue(field, value, shouldValidate))
    }

    return (
        <>
            <DateRange
                orientation={orientation}
                startValue={values.from}
                endValue={values.to}
                changeStartDate={date => updateFieldValue('from', date)}
                changeEndDate={date => updateFieldValue('to', date)}
                showLabel
            />
            <Field label={translate('registration.filter.user')} orientation={orientation}>
                <UserPicker
                    selectedOptions={values.users || []}
                    onOptionsSelect={(_, selectedOptions) => updateFieldValue('users', selectedOptions)}
                />
            </Field>
            {modules.has(ModuleType.Invoices) && (
                <Field label={translate('registration.filter.isBilled')} orientation={orientation}>
                    <BooleanCombobox
                        selectedOptions={[values.isBilled?.toString() || 'mixed']}
                        onOptionSelect={(_, data: CheckboxOnChangeData) =>
                            updateFieldValue('isBilled', data.checked === 'mixed' ? undefined : data.checked)
                        }
                    />
                </Field>
            )}
            {!props.hideFileField && (
                <Field label={translate('registration.filter.file')} orientation={orientation}>
                    <FilePicker
                        selectedOptions={values.files || []}
                        onOptionsSelect={(_, selectedOptions) => updateFieldValue('files', selectedOptions)}
                    />
                </Field>
            )}
            {modules.has(ModuleType.Attributes) && (
                <Accordion collapsible>
                    <AccordionItem value="attributes">
                        <AccordionHeader>{translate('registration.filter.attributes')}</AccordionHeader>
                        <AccordionPanel>
                            <AttributesList
                                orientation={orientation}
                                entityType={EntityAttributeType.File}
                                context={[AttributeContextType.File]}
                                viewType={AttributeViewType.Filter}
                                entity={values}
                                setFieldValue={(field, value) => updateFieldValue(values[field], value)}
                            />
                        </AccordionPanel>
                    </AccordionItem>
                </Accordion>
            )}
        </>
    )
}
const filterTagItems: FixedPriceFilterType[] = [
    {
        from: DateTime.local().toISODate(),
        to: DateTime.local().toISODate(),
        tag: 'day',
        icon: <CalendarDayRegular />,
        title: 'fixedprice.filter.today',
    },
    {
        from: DateTime.local().startOf('week').toISODate(),
        to: DateTime.local().endOf('week').toISODate(),
        tag: 'week',
        icon: <CalendarWorkWeekRegular />,
        title: 'fixedprice.filter.week',
    },
    {
        from: DateTime.local().startOf('month').toISODate(),
        to: DateTime.local().endOf('month').toISODate(),
        tag: 'month',
        icon: <CalendarMonthRegular />,
        title: 'fixedprice.filter.month',
    },
]

const headerSortingItems: FixedPriceSortingType[] = [
    { property: ['date', 'id'], direction: SortDirection.Ascending },
    { property: ['date', 'id'], direction: SortDirection.Descending, default: true },
    { property: ['description', 'id'], direction: SortDirection.Ascending, descriptionKey: 'sorting.descriptionaz' },
    { property: ['description', 'id'], direction: SortDirection.Descending, descriptionKey: 'sorting.descriptionza' },
    { property: ['file.name', 'id'], direction: SortDirection.Ascending, descriptionKey: 'sorting.fileaz' },
    { property: ['file.name', 'id'], direction: SortDirection.Descending, descriptionKey: 'sorting.fileza' },
    { property: ['file.reference', 'id'], direction: SortDirection.Ascending, descriptionKey: 'sorting.referenceasc' },
    {
        property: ['file.reference', 'id'],
        direction: SortDirection.Descending,
        descriptionKey: 'sorting.referencedesc',
    },
]

function HeaderFilterTags(props: FixedPriceOverviewProps) {
    const { values, setValues } = useFormikContext<FixedPriceFilterType>()
    const overviewStyles = useOverviewStyles()
    const { t: translate } = useTranslation()
    const { viewport, isViewport } = useViewport()

    const listItemSmall = useMemo<boolean>(() => isViewport('<', Viewport.XL), [viewport])

    return (
        <div className={overviewStyles.flexGap}>
            {filterTagItems.map((tag, index) => (
                <InteractionTag
                    key={index}
                    selected={values.tag === tag.tag}
                    onClick={() =>
                        setValues({
                            ...props.defaultFilterValues,
                            ...tag,
                        })
                    }
                    size={listItemSmall ? 'small' : 'medium'}
                >
                    <InteractionTagPrimary icon={tag.icon}> {translate(tag.title)}</InteractionTagPrimary>
                </InteractionTag>
            ))}
        </div>
    )
}

export default function BaseFixedPriceOverview(props: BaseFixedPriceOverviewProps) {
    const axios = useAxios()
    const itemStyles = useItemStyles()
    const { viewport, isViewport } = useViewport()

    const listItemSmall = useMemo<boolean>(() => isViewport('<', Viewport.XL), [viewport])

    const defaultFilterValues = useMemo<FixedPriceFilterType>(
        () => ({
            from: '',
            to: '',
            users: [],
            attributes: [],
            query: '',
            tag: 'day',
            ...props.fixedFilterValues,
            ...props.defaultFilterValues,
        }),
        [props.fixedFilterValues, props.defaultFilterValues],
    )

    const countItems = (column?: number, filterValues?: FixedPriceFilterType): Promise<FixedPriceOverviewCount> => {
        return axios.get('/fixed-price/count', { params: parseFilterValuesToParams(filterValues) }).then(response => response.data)
    }

    const loadItems = (
        column?: number,
        filterValues?: FixedPriceFilterType,
        sortingValues?: FixedPriceSortingType,
        start?: number,
        size?: number,
    ): Promise<ExtendedFixedPriceOverviewDto[]> => {
        return axios
            .get('/fixed-price', {
                params: { ...parseFilterValuesToParams(filterValues, sortingValues, start, size) },
            })
            .then((response: AxiosResponse<OverviewItem[]>): ExtendedFixedPriceOverviewDto[] =>
                (response?.data || []).map((item: OverviewItem) => ({
                    ...item,
                    turnover: item.rate,
                    invoiceTurnover: item.price && {
                        amount: item.price.subtotal,
                        currency: item.price.currency,
                    },
                })),
            )
    }

    return (
        <BaseOverview<ExtendedFixedPriceOverviewDto, FixedPriceFilterType, FixedPriceSortingType, FixedPriceOverviewCount>
            defaultFilterValues={defaultFilterValues}
            countItems={countItems}
            loadItems={loadItems}
            listItemHeaderContent={FixedPriceHeaderItem}
            listItemContent={(props: BaseFixedPriceLineOverviewProps) => <FixedPriceListItem {...props} />}
            listItemClassnames={() => [
                props.onItemSelected ? itemStyles.listFixedPriceItemSelectable : itemStyles.listFixedPriceItem,
                listItemSmall && (props.onItemSelected ? itemStyles.listFixedPriceItemSmallSelectable : itemStyles.listFixedPriceItemSmall),
            ]}
            headerDefaultFilterTags={HeaderFilterTags}
            headerSortingItems={headerSortingItems}
            filterContent={FixedPriceFilter}
            filterFromJson={values => ({
                ...values,
                from: values.tag ? generateFromDate(values.tag) : values.from,
                to: values.tag ? generateToDate(values.tag) : values.to,
            })}
            filterToJson={values => ({
                ...values,
                from: values.tag ? '' : values.from,
                to: values.tag ? '' : values.to,
                icon: undefined,
                title: undefined,
            })}
            inputPlaceholderKey={'fixedprice.filter.search'}
            {...props}
        />
    )
}
